import { env } from "@/lib/env";
import { getConfig } from "@/lib/runtime-config";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { mockRequest } from "@/api/mock";
import { saveBlob } from "@/lib/download";
import { useAppStore } from "@/store/app-store";

async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;
type FetchOptions = {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: Query): string {
  const base = getConfig().apiBaseUrl.replace(/\/$/, "");
  const url = new URL(`${base}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(
  method: string,
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  if (env.e2eMockApi) return mockRequest<T>(method, path, opts.body);

  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("X-Request-ID", crypto.randomUUID());
  const token = await getIdToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const selectedDepartmentId = useAppStore.getState().selectedDepartmentId;
  if (selectedDepartmentId) {
    headers.set("X-RespAi-Department-ID", selectedDepartmentId);
  }
  if (opts.body !== undefined) headers.set("Content-Type", "application/json");

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (error) {
    throw new ApiError(
      0,
      "Could not reach the RespAI API. Check the API URL, network, and CORS configuration.",
      error,
    );
  }

  if (res.status === 401) {
    try {
      await signOut();
    } catch {
      // ignore
    }
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new ApiError(401, "Unauthorized", null);
  }

  const isJson = res.headers.get("Content-Type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body
        ? (body as { detail: unknown }).detail
        : null;
    const message =
      (typeof detail === "string" ? detail : null) ||
      (body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : null) ||
      `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

/**
 * Authenticated binary download. The JSON `request` helper can't be used for
 * file exports (CSV/ZIP) because it parses the body as JSON and discards
 * non-JSON responses. This mirrors its auth headers and 401 handling, but
 * streams the response into a Blob and saves it via an anchor click.
 */
async function downloadBlob(
  path: string,
  opts: { query?: Query; filename: string },
): Promise<void> {
  if (env.e2eMockApi) {
    saveBlob(new Blob(["mock"], { type: "application/octet-stream" }), opts.filename);
    return;
  }

  const headers = new Headers();
  const token = await getIdToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const selectedDepartmentId = useAppStore.getState().selectedDepartmentId;
  if (selectedDepartmentId) {
    headers.set("X-RespAi-Department-ID", selectedDepartmentId);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method: "GET", headers });
  } catch (error) {
    throw new ApiError(
      0,
      "Could not reach the RespAI API. Check the API URL, network, and CORS configuration.",
      error,
    );
  }

  if (res.status === 401) {
    try {
      await signOut();
    } catch {
      // ignore
    }
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new ApiError(401, "Unauthorized", null);
  }

  if (!res.ok) {
    throw new ApiError(res.status, `HTTP ${res.status}`, null);
  }

  saveBlob(await res.blob(), opts.filename);
}

export const api = {
  get: <T>(path: string, opts?: FetchOptions) => request<T>("GET", path, opts),
  download: downloadBlob,
  post: <T>(path: string, body?: unknown, opts?: FetchOptions) =>
    request<T>("POST", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: FetchOptions) =>
    request<T>("PATCH", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: FetchOptions) =>
    request<T>("PUT", path, { ...opts, body }),
  delete: <T>(path: string, opts?: FetchOptions) =>
    request<T>("DELETE", path, opts),
};
